<template>
  <div class="mt-3">
    <div class="d-flex align-center">
      <span class="body-2 font-weight-medium text--primary">{{ title }}</span>
      <!-- Ikonipainike antaa hanskakäyttöön riittävän kosketusalueen; negatiivinen marginaali pitää otsikkorivin matalana.
           Puhelimessa ei ole hoveria: Vuetify ohittaa open-on-clickin, jos open-on-hover on päällä, joten se kytketään pois. -->
      <v-tooltip bottom open-on-click :open-on-hover="!$vuetify.breakpoint.xsOnly">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="my-n2" :aria-label="printedText" v-bind="attrs" v-on="on">
            <v-icon small class="no-gradient">{{ printed ? 'mdi-printer-outline' : 'mdi-printer-off-outline' }}</v-icon>
          </v-btn>
        </template>
        <span>{{ printedText }}</span>
      </v-tooltip>
    </div>
    <div class="caption text--secondary">{{ description }}</div>
    <!-- Puhelimessa rivit listana: viisisarakkeinen taulukko ei mahdu, ja lisätieto tarvitsee koko leveyden. -->
    <v-list v-if="$vuetify.breakpoint.xsOnly" dense class="py-0 mt-1">
      <template v-for="(worker, idx) in workers">
        <v-divider v-if="idx > 0" :key="`divider-${idx}`" />
        <v-list-item :key="idx" class="px-0">
          <v-list-item-content>
            <!-- Sama 14px tekstikoko kuin työpöydän taulukossa; ryhmäotsikko erottuu painolla. -->
            <div class="d-flex body-2">
              <span>{{ worker.firstname }} {{ worker.lastname }}</span>
              <v-spacer />
              <span class="text--secondary text-no-wrap ml-2">{{ timeRange(worker) }} · {{ $formatHoursAsHHMM(worker.workHours) }}</span>
            </div>
            <div v-if="worker.info" class="mt-1 body-2 daybook-time-entries__info">{{ worker.info }}</div>
            <div v-if="(worker.equipments || []).length > 0" class="caption text--secondary mt-1">
              <template v-for="(eq, i) in worker.equipments">
                <span :key="eq.id">
                  {{ equipmentName(eq) }} <span v-if="eq.licenseNumber" class="text-no-wrap">({{ eq.licenseNumber }})</span
                  >{{ i < worker.equipments.length - 1 ? ',' : '' }}
                </span>
              </template>
            </div>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>
    <v-simple-table v-else dense class="mt-1 daybook-time-entries__table">
      <template v-slot:default>
        <colgroup>
          <col class="daybook-time-entries__col-name" />
          <col class="daybook-time-entries__col-time" />
          <col class="daybook-time-entries__col-hours" />
          <col class="daybook-time-entries__col-equipment" />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th>{{ $t('daybook.workers_name') }}</th>
            <th>{{ $t('daybook.entry_times') }}</th>
            <th>h</th>
            <th>{{ $t('daybook.equipments_title') }}</th>
            <th>{{ $t('daybook.info') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(worker, idx) in workers" :key="idx">
            <td>{{ worker.firstname }} {{ worker.lastname }}</td>
            <td class="text-no-wrap">{{ timeRange(worker) }}</td>
            <td class="text-no-wrap">{{ $formatHoursAsHHMM(worker.workHours) }}</td>
            <td>
              <!-- Rekisterinumero ei saa katketa tavuviivan kohdalta. -->
              <template v-for="(eq, i) in worker.equipments || []">
                <span :key="eq.id">
                  {{ equipmentName(eq) }} <span v-if="eq.licenseNumber" class="text-no-wrap">({{ eq.licenseNumber }})</span
                  >{{ i < worker.equipments.length - 1 ? ',' : '' }}
                </span>
              </template>
            </td>
            <td class="daybook-time-entries__info">{{ worker.info }}</td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import { equipmentName, isPrintedToCustomer } from '@/helpers/daybookTimeEntries';

// Yhden kirjauksen tyypin (URAKKA / TUNTITYO / ODOTUS) ryhmä; otsikko ja selite samat kuin tuntikirjausdialogissa.
export default {
  name: 'DaybookTimeEntries',
  props: {
    chargeType: { type: String, required: true },
    workers: { type: Array, required: true },
  },
  computed: {
    title() {
      return `${this.$t('timer.charge_types.' + this.chargeType)} (${this.workers.length} ${this.$t('daybook.workers_count')})`;
    },
    description() {
      return this.$t('timer.charge_type_descriptions.' + this.chargeType);
    },
    printed() {
      return isPrintedToCustomer(this.chargeType);
    },
    printedText() {
      return this.printed ? this.$t('daybook.printed_to_customer') : this.$t('daybook.not_printed_to_customer');
    },
  },
  methods: {
    equipmentName,
    timeRange(worker) {
      return `${this.$formatTimeNoDate(worker.entryStart)}–${this.$formatTimeNoDate(worker.entryEnd)}`;
    },
  },
};
</script>

<style scoped>
.daybook-time-entries__col-name {
  width: 20%;
}
.daybook-time-entries__col-time {
  width: 15%;
}
.daybook-time-entries__col-hours {
  width: 8%;
}
.daybook-time-entries__col-equipment {
  width: 22%;
}
/* Kirjauksen lisätieto voi olla monirivinen; rivinvaihdot säilytetään. */
.daybook-time-entries__info {
  white-space: pre-line;
}
</style>
